import { CatalogFlow, normalizeCatalogNode } from './catalogDoc';
import { GraphDevice } from './graph';
import {
  CatalogWizardArgs,
  asGroup,
  ensureBus,
  ensureController,
  ensureDashboard,
  ensureDevice,
  ensurePoint,
  ensureTimer,
  finishChain,
  link,
  startChain,
  upsertDevice,
  wireToMqtt,
} from './catalogChain';

function climatePoints(device: GraphDevice): { key: string; index: number }[] {
  const raw = Array.isArray(device.points) ? device.points : [];
  const listed = raw.map((item, index) => {
    const row = item && typeof item === 'object' ? item as { key?: string; index?: number } : {};
    const key = String(row.key || '').trim();
    return { key, index: Number(row.index) >= 0 ? Number(row.index) : index };
  }).filter((item) => item.key);
  if (listed.length) {
    return listed;
  }
  return [
    { key: 'temp', index: 0 },
    { key: 'rh', index: 1 },
  ];
}

/** Dual-write Modbus master group and a remote slave instance. Pins stay in settings. */
export function applyModbusSlaveHardware(
  values: Record<string, unknown>,
  device: GraphDevice,
): Record<string, unknown> {
  const requested = String(device.connection || '').trim();
  const connection = !requested || requested === 'modbus' ? 'rs485' : requested;
  const points = climatePoints(device);
  const instance: GraphDevice = {
    ...device,
    id: String(device.id || 'climate').trim() || 'climate',
    type: 'modbus_slave',
    enable: device.enable !== false,
    connection,
    unit: device.unit ?? 1,
    fc: device.fc ?? 3,
    address: device.address ?? 0,
    count: Math.max(Number(device.count) || 0, points.length, 1),
    points,
  };
  return {
    ...values,
    modbus: { ...asGroup(values.modbus), enable: true, role: 'master' },
    mqtt: { ...asGroup(values.mqtt), enable: true },
    devices: upsertDevice(values, instance),
  };
}

export function mergeModbusCatalog(doc: CatalogFlow, args: CatalogWizardArgs): CatalogFlow {
  const state = startChain(doc);
  const of = String(args.device.id || 'climate').trim() || 'climate';
  const requested = String(args.device.connection || '').trim();
  const connection = !requested || requested === 'modbus' ? 'rs485' : requested;
  const interval = Number(args.interval) > 0 ? Math.round(Number(args.interval)) : 60;
  const points = climatePoints(args.device);
  const controller = ensureController(state, args);
  const bus = ensureBus(state, {
    of: connection,
    busType: 'modbus',
    role: 'master',
    x: state.x0 + 160,
    y: state.y0,
  });
  const device = ensureDevice(state, {
    of,
    deviceType: 'modbus_slave',
    x: state.x0 + 320,
    y: state.y0,
  });
  const timer = ensureTimer(state, interval, state.x0, state.y0 + 220);
  link(state, `${controller.id}.host`, `${bus.id}.host`);
  link(state, `${bus.id}.read`, `${device.id}.read`);
  const scale = { op: '*', value: 0.1 };
  const mqttByKey: Record<string, { id: string }> = {};
  points.forEach((point, index) => {
    const node = ensurePoint(state, {
      of,
      key: point.key,
      poll: true,
      x: state.x0 + 500,
      y: state.y0 + index * 140,
      deviceId: device.id,
    });
    mqttByKey[point.key] = wireToMqtt(state, {
      point: node,
      topic: `{base}/${of}/${point.key}`,
      y: state.y0 + index * 140,
      xCalc: state.x0 + 680,
      xMqtt: state.x0 + 840,
      timer,
      scale,
    });
  });
  if (args.widget === 'temperature') {
    const dash = ensureDashboard(state, {
      widgetType: 'temperature',
      title: args.hostname ? `${args.hostname} · ${of}` : of,
      x: state.x0 + 1020,
      y: state.y0 + 70,
    });
    const tempKey = points.find((item) => item.key === 'temp' || item.key === 'temperature')?.key || points[0]?.key;
    const rhKey = points.find((item) => item.key === 'rh' || item.key === 'humidity')?.key;
    if (tempKey && mqttByKey[tempKey]) {
      link(state, `${mqttByKey[tempKey].id}.subscribe`, `${dash.id}.in`);
    }
    if (rhKey && mqttByKey[rhKey]) {
      link(state, `${mqttByKey[rhKey].id}.subscribe`, `${dash.id}.inRh`);
    }
  }
  const next = finishChain(doc, args, state);
  return { ...next, nodes: next.nodes.map(normalizeCatalogNode) };
}
